import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import {
  Assignable,
  ProjectElementType,
  Removable,
  Identifiable,
  Event as E,
  resource,
  OptimizedGantt,
} from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { useAssignTag, useEditEvent } from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { EventIcon } from "@common/eventIcon";
import { ActivityWatcher } from "@common/activityWatcher";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { AllocationUnit } from "@common/allocationUnit";
import { useIsElementHovered } from "@common/elementHover";
import { reCalculateDateRange } from "../utils";
import { CommentsCount } from "../commentsCount";
import { AttachmentsCount } from "../attachmentsCount";
import { DateForm } from "../dateForm";
import { SecondaryCriteria } from "../secondaryCriteria";
import { Tags } from "../tags";
import { useProjectCtx } from "../projectCtx";
import { Props } from "./types";
import { NameEditionForm } from "./nameEditionForm";
import styles from "./styles.scss";

function toFixed(nbr: number) {
  return typeof nbr === "number" || !isNaN(parseFloat(nbr))
    ? Math.round(nbr) === nbr
      ? nbr
      : nbr.toFixed(2)
    : "";
}

function projectElementToEvent(
  element: Extract<OptimizedGantt[number], { type: ProjectElementType.EVENT }>,
): Identifiable<E> {
  return {
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    startTime: element.startDate,
    endTime: element.endDate,
    createdDate: element.createdDate || "",
    tags: element.tags,
    keyRank: element.referenceKeyRank,
    attributes: element.attributes,
    organizer: { ...resource, uuid: "" },
    participants: [],
  };
}

function Event({
  event,
  meta,
  parentList,
  isMultiProject,
  minDate,
  maxDate,
}: Props) {
  const userTimezone = DateTime.local().zoneName;
  const realEvent = projectElementToEvent(event);
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { project, template } = useProjectCtx();
  const projectTimezone = project.timeZone || userTimezone;
  const isHovered = useIsElementHovered(event.uuid);
  const { isRequestPending, editEvent } = useEditEvent(realEvent);
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const { assignTag } = useAssignTag();
  const isDone =
    DateTime.fromISO(event.endDate, { zone: "utc" }) < DateTime.now();
  const recalculated = reCalculateDateRange(event, minDate, maxDate);

  ActivityWatcher.useActivityWatcher(ref, {
    focus: () => setShowContextualMenu(!showContextualMenu),
    blur: () => setShowContextualMenu(false),
  });

  return (
    <div
      className={classNames(styles.event, {
        [styles.done]: isDone,
        [styles.hovered]: isHovered,
      })}
    >
      <SecondaryCriteria project={project}>
        {() => ({
          main: (
            <div
              className={styles.eventDetails}
              onClick={() =>
                pushView({
                  key: UseCases.retrieveEvent,
                  data: { event: realEvent, isMultiProject },
                })
              }
            >
              <div className={styles.name}>
                <div
                  className={styles.eventCompletionStatus}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <EventIcon className={styles.icon} />
                </div>
                <NameEditionForm event={realEvent} hovered={isHovered} />
              </div>
            </div>
          ),
          criteria: {
            tags: (
              <div className={styles.tags}>
                <Tags
                  tags={event.tags}
                  project={project}
                  removable={Removable.EVENT}
                  uuid={event.uuid}
                  add={(tag) =>
                    assignTag({
                      tag,
                      assignable: Assignable.EVENT,
                      assignableId: event.uuid,
                      project,
                    })
                  }
                />
              </div>
            ),
            allocated: (
              <div className={styles.allocated}>
                {toFixed(meta.allocatedHours)}
                <AllocationUnit amount={meta.allocatedHours} format="symbol" />
              </div>
            ),
            spent: <div className={styles.allocated}></div>,
            startDate: (
              <DateForm
                projectElement={event}
                projectTimezone={projectTimezone}
                date={event.startDate}
                recalculatedDate={recalculated.startDate}
                disabled={isRequestPending}
                minDate={(() => {
                  const bound = minDate || event.minStartDate;
                  if (!bound) return undefined;
                  const dt = DateTime.fromISO(bound, {
                    zone: "utc",
                  }).setZone(userTimezone);
                  return dt.isValid
                    ? { year: dt.year, month: dt.month - 1, day: dt.day }
                    : undefined;
                })()}
                maxDate={(() => {
                  const bound = event.endDate || maxDate || event.maxEndDate;
                  if (!bound) return undefined;
                  const dt = DateTime.fromISO(bound, {
                    zone: "utc",
                  }).setZone(userTimezone);
                  return dt.isValid
                    ? { year: dt.year, month: dt.month - 1, day: dt.day }
                    : undefined;
                })()}
                submit={(startTime) =>
                  editEvent({
                    event: realEvent,
                    nextEvent: { ...realEvent, startTime },
                  })
                }
              />
            ),
            endDate: (
              <DateForm
                projectElement={event}
                projectTimezone={projectTimezone}
                date={event.endDate}
                recalculatedDate={recalculated.endDate}
                disabled={isRequestPending}
                minDate={(() => {
                  const bound =
                    event.startDate || minDate || event.minStartDate;
                  if (!bound) return undefined;
                  const dt = DateTime.fromISO(bound, {
                    zone: "utc",
                  }).setZone(userTimezone);
                  return dt.isValid
                    ? { year: dt.year, month: dt.month - 1, day: dt.day }
                    : undefined;
                })()}
                submit={(endTime) =>
                  editEvent({
                    event: realEvent,
                    nextEvent: { ...realEvent, endTime },
                  })
                }
              />
            ),
            ...template.reduce(
              (acc, criteria) => ({
                ...acc,
                [criteria.uuid]: (
                  <SecondaryCriteriaForm
                    criteria={criteria}
                    value={
                      event.attributes
                        ? event.attributes[criteria.uuid] || ""
                        : ""
                    }
                    projectTimezone={projectTimezone}
                    disabled={isRequestPending}
                    submit={(value) =>
                      editEvent({
                        event: realEvent,
                        nextEvent: {
                          ...realEvent,
                          attributes: {
                            ...event.attributes,
                            [criteria.uuid]: value,
                          },
                        },
                      })
                    }
                  />
                ),
              }),
              {},
            ),
          },
          comments: (
            <CommentsCount
              subject={event.uuid}
              type="event"
              commentsNbr={meta.commentCount}
            />
          ),
          files: (
            <AttachmentsCount
              uuid={event.uuid}
              fileNbr={meta.attachmentCount}
            />
          ),
          contextualMenu: (
            <div
              className={classNames(styles.contextualMenu, {
                [styles.displayContextualMenu]: showContextualMenu,
              })}
            >
              <div ref={ref}>
                <SecureContextualMenu
                  context={{ event: realEvent }}
                  replacement={<div className={styles.placeholder} />}
                  items={[
                    {
                      name: t("listsList.contextualMenu.edit"),
                      useCase: UseCases.editEvent,
                      // renderIcon: (className) => (
                      //   <EditIcon className={className} />
                      // ),
                      act: () =>
                        pushView({
                          data: { event: realEvent, isMultiProject },
                          key: UseCases.retrieveEvent,
                        }),
                    },
                    {
                      name: t("listsList.contextualMenu.move"),
                      useCase: UseCases.moveListElement,
                      // renderIcon: (className) => (
                      //   <TransferIcon className={className} />
                      // ),
                      act: () =>
                        pushView({
                          key: UseCases.moveListElement,
                          data: {
                            projectElementId: event.uuid,
                            projectElementType: ProjectElementType.EVENT,
                            currentParentList: parentList,
                            project,
                          },
                        }),
                    },
                    {
                      name: t("listsList.contextualMenu.remove"),
                      useCase: UseCases.removeEvent,
                      // renderIcon: (className) => (
                      //   <TrashIcon
                      //     className={classNames(className, styles.removeIcon)}
                      //   />
                      // ),
                      color: "#db5f63",
                      act: () =>
                        pushView({
                          data: realEvent,
                          key: UseCases.removeEvent,
                        }),
                    },
                  ]}
                />
              </div>
            </div>
          ),
        })}
      </SecondaryCriteria>
    </div>
  );
}

export { Event };
